import { DocumentMeta } from './meta';
import type { PaymentStatus } from './static/types';
import { sum } from './utils';
import { Waterfall } from './waterfall/waterfall';

export interface Expense {
  _meta?: DocumentMeta;
  id: string;
  date: Date;
  /** The contract linked to this expense */
  contractId: string;
  rightholderId: string;
  titleId: string; // equals waterfallId
  status: PaymentStatus;
  price: number;
  version: Record<string, { hidden?: true, price: number }>;
  typeId: string;
  nature: string;
  capped: boolean;
}

export function createExpense(params: Partial<Expense> = {}): Expense {
  return {
    id: '',
    contractId: '',
    rightholderId: '',
    titleId: '',
    status: 'pending',
    price: 0,
    version: {},
    date: new Date(),
    typeId: '',
    nature: '',
    capped: false,
    ...params,
  };
}

interface ExpenseCap {
  default: number;
  version: Record<string, number>;
}

export interface ExpenseType {
  id: string;
  name: string;
  contractId: string;
  cap: ExpenseCap;
}

export function createExpenseType(params: Partial<ExpenseType> = {}): ExpenseType {
  return {
    id: '',
    name: '',
    contractId: '',
    ...params,
    cap: createExpenseCap(params.cap),
  };
}

function createExpenseCap(params: Partial<ExpenseCap> = {}): ExpenseCap {
  return {
    default: params.default ?? 0,
    version: params.version ?? {},
  };
}

export function expensesPrice(_expenses: Partial<Expense>[], typeId: string, cap = 0): number {
  const expenses = _expenses.filter(e => e.typeId === typeId);

  let capped = 0;
  let uncapped = 0;

  if (cap > 0) {
    capped = Math.min(sum(expenses.filter(e => e.capped).map(e => e.price)), cap);
    uncapped = sum(expenses.filter(e => !e.capped).map(e => e.price));
  } else {
    uncapped = sum(expenses.map(e => e.price));
  }

  return capped + uncapped;
}

export function expenseTypeCap(expenseType: ExpenseType, versionId?: string): number {
  if (!expenseType) return undefined;
  if (!versionId) return expenseType.cap.default;
  return expenseType.cap.version[versionId] ?? expenseType.cap.default;
}

export function isExpenseTypeCapped(expenseType: ExpenseType, versionId?: string): boolean {
  if (!expenseType) return false;
  return expenseTypeCap(expenseType, versionId) > 0;
}

export function getAllExpenseTypes(waterfall: Waterfall) {
  return Object.values(waterfall.expenseTypes).flat();
}

export function getExpenseTypes(contractId: string, waterfall: Waterfall) {
  return waterfall.expenseTypes[contractId || 'directSales'] || [];
}

export function getExpenseTypeName(typeId: string, contractId: string, waterfall: Waterfall) {
  return getExpenseTypes(contractId, waterfall).find(type => type.id === typeId)?.name || '--';
}

export function getExpenseCapClass(amount: number, cap: number) {
  if (amount / cap <= 0.8) return 'expense-ok';
  if (amount / cap >= 1) return 'expense-alert';
  if (amount / cap > 0.8) return 'expense-warning';
}

/**
 * Return expenses with the same titleId, contractId, rightholderId, typeId, nature and capped
 * @param subject 
 * @param incomes 
 */
export function getMatchingExpenses(subject: Expense, expenses: Expense[]): Expense[] {
  return expenses.filter(p => {
    if (p.titleId !== subject.titleId) return false;
    if (p.contractId !== subject.contractId) return false;
    if (p.rightholderId !== subject.rightholderId) return false;
    if (p.typeId !== subject.typeId) return false;
    if (p.nature !== subject.nature) return false;
    return p.capped === subject.capped;
  });
}

export function mergeMatchingExpenses(_expenses: Expense[]): Expense[] {
  const expenses: Expense[] = [];
  for (const _expense of _expenses) {
    const expense = createExpense({ ..._expense });
    if (getMatchingExpenses(expense, expenses).length > 0) continue; // Already processed
    const duplicates = getMatchingExpenses(expense, _expenses);
    if (!expense.id && duplicates.find(d => !!d.id)) {
      expense.id = duplicates.find(d => !!d.id).id;
    }
    expense.price = sum(duplicates, d => d.price);
    expenses.push(expense);
  }
  return expenses;
}